import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { useFormikContext } from 'formik';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { IFormValues } from './ModalCreateCustomDashboard';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { reducerUpdateCurrentScreen } from 'src/features/customDashboard';
import { IObject } from 'src/models/objectManagement';
import GetSocialMediaIcon from 'src/components/GetSocialMediaIcon';
import { getSymbolByStreamType } from 'src/utils/getSymbolByStreamType';
import { convertExactObjectName } from 'src/utils/convertExactObjectName';

const SelectObjectOrGroupSection = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { values, setFieldValue } = useFormikContext<IFormValues>();
  const selectedGroups = values.objectGroups;
  const selectedObjects = values.objects;

  const handleClickAdd = (type: 'object' | 'group') => {
    dispatch(reducerUpdateCurrentScreen(type));
  };

  const handleDeleteGroup = (id: string) => {
    const newGroups = selectedGroups.filter((group) => group.id !== id);
    setFieldValue('objectGroups', newGroups);
  };

  const handleDeleteObject = (id: string) => {
    const nextObjects = selectedObjects.filter((obj) => obj.id !== id);
    setFieldValue('objects', nextObjects);
  };

  const renderGroupChips = (
    items: { id: string; label: string }[],
    onDelete: (id: string) => void
  ) => {
    const numOfItemsToShow = 4;
    const chipsToShow = items.slice(0, numOfItemsToShow);
    const extraCount = items.length - numOfItemsToShow;

    return (
      <>
        {chipsToShow.map((item) => (
          <Chip
            key={item.id}
            label={item.label}
            onDelete={() => onDelete(item.id)}
            deleteIcon={<CloseIcon />}
            sx={{
              padding: `10px ${theme.spacing(1)}`,
              maxWidth: items.length > 1 ? '150px' : '250px',
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.main,
              '& .MuiChip-deleteIcon': {
                color: theme.palette.primary.main,
                fontSize: '16px'
              },
              '& .MuiChip-deleteIcon:hover': {
                color: theme.palette.primary.main
              },
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block'
              }
            }}
          />
        ))}
        {extraCount > 0 && (
          <Chip
            label={`+${extraCount}`}
            sx={{
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.main,
              fontWeight: 600
            }}
          />
        )}
      </>
    );
  };

  const renderObjectChips = (
    items: IObject[],
    onDelete: (id: string) => void
  ) => {
    const numOfItemsToShow = 4;
    const chipsToShow = items.slice(0, numOfItemsToShow);
    const extraCount = items.length - numOfItemsToShow;

    return (
      <>
        {chipsToShow.map((item) => (
          <Chip
            key={item.id}
            label={`${getSymbolByStreamType(
              item.streamType
            )}${convertExactObjectName(item.objectName)}`}
            onDelete={() => onDelete(item.id)}
            deleteIcon={<CloseIcon />}
            avatar={
              <GetSocialMediaIcon
                socialMedia={item.socialMedia}
                sx={{ color: theme.colors.primary.main, fontSize: 16 }}
                isSolidColor={item.socialMedia === 'googleReview'}
              />
            }
            sx={{
              maxWidth: items.length > 1 ? '200px' : '250px',
              padding: `10px ${theme.spacing(1)}`,
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.main,
              '& .MuiChip-deleteIcon': {
                color: theme.palette.primary.main,
                fontSize: '16px'
              },
              '& .MuiChip-deleteIcon:hover': {
                color: theme.palette.primary.main
              },
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block'
              }
            }}
          />
        ))}
        {extraCount > 0 && (
          <Chip
            label={`+${extraCount}`}
            sx={{
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.main,
              fontWeight: 600
            }}
          />
        )}
      </>
    );
  };

  const {
    isOnTour,
    currentTour: { stepNumber, menu }
  } = useAppSelector((state) => state.storeProductTour);

  return (
    <Stack gap={2}>
      <Typography variant="h5" textAlign="center">
        Add object or group to visualize your data on your dashboard for the
        better insights.
      </Typography>
      <Card
        variant="outlined"
        sx={{ border: `1px solid ${theme.palette.grey[500]}` }}
      >
        <CardContent
          sx={{
            padding: theme.spacing(2),
            ':last-child': {
              paddingBottom: theme.spacing(2)
            }
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Box>
              <Typography variant="body1" fontWeight="600">
                Account, Keyword & Hashtag
              </Typography>
              <Typography variant="caption">
                Select any object social media to monitor.
              </Typography>
              {selectedObjects.length > 0 && (
                <Stack
                  direction="row"
                  gap={0.5}
                  width="100%"
                  flexWrap="nowrap"
                  py={1}
                  sx={{
                    overflow: 'scroll',
                    scrollbarWidth: 'none'
                  }}
                >
                  {renderObjectChips(selectedObjects, handleDeleteObject)}
                </Stack>
              )}
            </Box>
            <Box>
              <Button
                variant="contained"
                endIcon={<AddIcon />}
                onClick={!isOnTour ? () => handleClickAdd('object') : undefined}
                disabled={selectedGroups.length > 0}
              >
                Add
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <Card
        variant="outlined"
        sx={{ border: `1px solid ${theme.palette.grey[500]}` }}
      >
        <CardContent
          sx={{
            padding: theme.spacing(2),
            ':last-child': {
              paddingBottom: theme.spacing(2)
            }
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Box>
              <Typography variant="body1" fontWeight="600">
                Group
              </Typography>
              <Typography variant="caption">
                Select social media group to monitor.
              </Typography>
              {selectedGroups.length > 0 && (
                <Stack
                  direction="row"
                  gap={0.5}
                  width="100%"
                  flexWrap="nowrap"
                  py={1}
                  sx={{
                    overflow: 'scroll',
                    scrollbarWidth: 'none'
                  }}
                >
                  {renderGroupChips(
                    selectedGroups.map((group) => ({
                      id: group.id,
                      label: group.displayName
                    })),
                    handleDeleteGroup
                  )}
                </Stack>
              )}
            </Box>
            <Box>
              <Button
                variant="contained"
                endIcon={<AddIcon />}
                onClick={!isOnTour ? () => handleClickAdd('group') : undefined}
                disabled={selectedObjects.length > 0}
              >
                Add
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default SelectObjectOrGroupSection;
